import { ArrowDownwardOutlined, ArrowUpwardOutlined } from "@mui/icons-material";
import {
    Box,
    Button,
    Grid,
    IconButton,
    Paper,
    TableBody,
    TableContainer,
    TableRow,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetProductsBookedDays } from "../../../services/productsService";
import { StyledTableCell, StyledTableHead } from "../../table/BaseTable/BaseTable";
import { DatePeriodSelector, isPeriodValid } from "../DatePeriodSelector";
import { BookingNoteInput } from "./BookingNoteInput";
import { StatusButton } from "./BookingStatusButton";

export const CreateBookingForm = (props) => {
    const {
        apartment,
        apartmentChildProducts = [],
        extraChildProducts = [],
        onMoveChildProductDown,
        onMoveChildProductUp,
        onSubmit,
        isFirst = false,
        isLast = false,
    } = props;
    const { t } = useTranslation();

    const [note, setNote] = useState("");

    const today = new Date();
    const initialEndDate = new Date();
    initialEndDate.setDate(today.getDate() + 2);

    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(initialEndDate);

    const nonMovableProducts = [apartment, ...apartmentChildProducts];
    const allProducts = [apartment, ...apartmentChildProducts, ...extraChildProducts];
    const allProductIds = allProducts.map((p) => p.id);

    const bookingDurationLimits = allProducts.map((p) => p.investment.invest_booking_duration_days);
    const maxDuration =
        bookingDurationLimits.length > 0 ? Math.min(...bookingDurationLimits) : undefined;

    const handleSubmit = () => {
        onSubmit({
            productIds: allProductIds,
            note,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
        });
    };

    const { data: excludedDates } = useGetProductsBookedDays(allProductIds, null, {
        select: (data) =>
            data.booked_days.map(
                (day) =>
                    new Date(
                        new Date(day).getFullYear(),
                        new Date(day).getMonth(),
                        new Date(day).getDate(),
                    ),
            ),
    });

    const canSubmit = isPeriodValid(startDate, endDate, excludedDates, maxDuration);

    return (
        <BookingFormLayout
            table={
                <ProductsTable
                    products={nonMovableProducts}
                    movableProducts={extraChildProducts}
                    onMoveProductDown={onMoveChildProductDown}
                    onMoveProductUp={onMoveChildProductUp}
                    isFirst={isFirst}
                    isLast={isLast}
                />
            }
            noteInput={<BookingNoteInput note={note} onChange={setNote} />}
            periodSelector={
                <DatePeriodSelector
                    startDate={startDate}
                    endDate={endDate}
                    excludedDates={excludedDates}
                    onStartDateChange={setStartDate}
                    onEndDateChange={setEndDate}
                    maxDuration={maxDuration}
                />
            }
            button={
                <Button
                    variant="outlined"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    sx={{ width: "100%", marginRight: "6rem" }}
                >
                    {t("CreateBookingForm.createBooking")}
                </Button>
            }
        />
    );
};

/**
 * Read-only version of CreateBookingForm for existing bookings.
 */
export const BookingReadonlyDisplay = (props) => {
    const { booking, readOnly } = props;

    return (
        <BookingFormLayout
            table={<ProductsTable products={booking.products} />}
            noteInput={<BookingNoteInput note={booking.note} readonly={true} />}
            periodSelector={
                <DatePeriodSelector
                    startDate={new Date(booking.start)}
                    endDate={new Date(booking.end)}
                    readOnly={true}
                />
            }
            button={!readOnly ? <StatusButton booking={booking} /> : null}
        />
    );
};

const BookingFormLayout = (props) => {
    const { table, noteInput, periodSelector, button } = props;

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={4}>
                <Grid item xs={6}>
                    <Grid container direction="column" spacing={2}>
                        <Grid item>{table}</Grid>
                        <Grid item>{noteInput}</Grid>
                    </Grid>
                </Grid>
                <Grid item xs={6}>
                    <Grid
                        container
                        direction="column"
                        spacing={2}
                        sx={{ display: "flex", alignItems: "center" }}
                    >
                        <Grid item>{periodSelector}</Grid>
                        <Grid
                            item
                            sx={{
                                width: "50%",
                            }}
                        >
                            {button}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
};

export const ProductsTable = (props) => {
    const {
        products,
        movableProducts,
        onMoveProductUp,
        onMoveProductDown,
        isFirst = false,
        isLast = false,
    } = props;
    const { t } = useTranslation();

    return (
        <TableContainer sx={{ width: "100%" }}>
            <StyledTableHead>
                <TableRow>
                    <StyledTableCell sx={{ width: "6rem" }} />
                    <StyledTableCell sx={{ width: "10rem" }}>
                        {t("products.columns.number")}
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: "10rem" }}>
                        {t("products.columns.productType")}
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: "10rem" }}>
                        {t("products.columns.investment")}
                    </StyledTableCell>
                </TableRow>
            </StyledTableHead>
            <TableBody>
                {products.map((product) => (
                    <TableRow key={product.id}>
                        <StyledTableCell />
                        <StyledTableCell>{product.product_number}</StyledTableCell>
                        <StyledTableCell>{product.type?.name}</StyledTableCell>
                        <StyledTableCell>
                            {product.investment.investment_name_sales}
                        </StyledTableCell>
                    </TableRow>
                ))}
                {movableProducts?.map((product) => (
                    <TableRow key={product.id}>
                        <StyledTableCell sx={{ width: "6rem" }}>
                            <Box>
                                {!isFirst && onMoveProductUp ? (
                                    <IconButton onClick={() => onMoveProductUp(product)}>
                                        <ArrowUpwardOutlined />
                                    </IconButton>
                                ) : null}
                                {!isLast && onMoveProductDown ? (
                                    <IconButton onClick={() => onMoveProductDown(product)}>
                                        <ArrowDownwardOutlined />
                                    </IconButton>
                                ) : null}
                            </Box>
                        </StyledTableCell>
                        <StyledTableCell>{product.product_number}</StyledTableCell>
                        <StyledTableCell>{product.type?.name}</StyledTableCell>
                        <StyledTableCell>
                            {product.investment.investment_name_sales}
                        </StyledTableCell>
                    </TableRow>
                ))}
            </TableBody>
        </TableContainer>
    );
};
